// Model report -> EKRM form: the readers a wizard built out of one or more model reports needs.
//
// This module was written for the one-shot clone — "make this one model an EKRM" — and outlived it.
// What is left is everything that reads a form back: what the media spend totals, what the
// marketing attribution comes to, where a lever may be moved to before the payload stops being
// valid, and what a ROAS the user asks for costs. None of it cares how the form was made, which is
// what lets CreateModelCompose and src/utils/ekrmCompose.js — where the channels come from any
// number of models — share every one of them.
//
// Everything here is pure, for the same reason src/utils/ekrmFromModel.js is: the component that
// calls it cannot be mounted in a test (vue and vue-template-compiler are a patch apart, so
// @vue/test-utils refuses to load and no component in this project can be mounted), so the build
// has to be reachable without Vue or it is not covered at all.
//
// Nothing is re-implemented here: the cells come from importGlobalCells and the rows from
// derivedAttributionRows, the same functions the wizards reach through their own watchers.

import { derivedAttributionRows } from '@/utils/ekrmDefinition.js'
import {
  gridSpends,
  hillAt,
  hillCurvePoints,
  hillDiminishingReturns,
  hillSamples,
  curveGridPoint,
  isHillParams,
  sliderBounds
} from '@/utils/hillCurve.js'
import {
  CALENDAR_AVERAGE,
  band,
  importGlobalCells,
  kpiNames,
  toCell,
  toCells,
  toRevenueCells
} from '@/utils/ekrmFromModel.js'

// The window a seasonality is read over. Averaging every January together across the history is
// what "Avg. Monthly Revenue" means and what keeps the seasonality intact, where the last twelve
// months would carry whatever happened to be true of the final year.
const CLONE_MODE = CALENDAR_AVERAGE
// The one currency an EKRM is ever denominated in, on both axes: its media spend and its KPI are
// both USD and the backend has no field in which a payload could say otherwise. A source model is
// under no such rule — it is fitted in whatever the client reports in, and its two axes need not
// even agree with each other — so anything not already this has to be converted on the way in, and
// the rate is the one thing the report cannot supply.
export const EKRM_UNIT = 'USD'

// A marketing attribution above this is refused rather than written. The row it lands in is summed
// against the others by validateEkrmForm, which errors at 100, and a share that far out means the
// user has driven the spend somewhere the source model cannot speak about at all.
export const MAX_ATTRIBUTION_SHARE = 95

// The bracket solveSpendFactor searches for a spend multiplier, and how finely. Logarithmic, because
// what a user means by "reach this ROAS" spans orders of magnitude in either direction, and sampled
// before it bisects because the aggregate of several Hill curves is not monotone: one with an
// exponent above 1 is convex at the bottom, so its average return rises before it falls.
const SOLVE_MIN_FACTOR = 1e-3
const SOLVE_MAX_FACTOR = 1e3
const SOLVE_SAMPLES = 121
const SOLVE_ITERATIONS = 60

// The smallest share of its own spend the mix solver may leave a channel with. The bounds that
// matter are read off each channel's own curve — see mixBounds — and this is the floor under both of
// them: an EKRM has to be able to speak about every medium it carries, and a row of rounding dust is
// not a medium.
const MIN_MIX_FACTOR = 0.01

// How finely the mix solver searches: the rungs one channel may be moved between, spaced
// logarithmically across the range its own curve prices, and the most mixes it will weigh up. The
// prices themselves are not sampled at all — see mixCandidates — but a model with a dozen channels
// has a few hundred of them and each one costs a full solve, so the frontier is thinned to an even
// spread when it comes out longer than this.
const MIX_CANDIDATES = 81
const MIX_MIXES = 64

// How many levels a mix is read at while it is only being SIFTED — is the return the user asked for
// anywhere between the worst and the best this mix can do? A third of the bracket the solver itself
// walks, because a mix that passes is then solved properly and a mix that fails is never looked at
// again. Sound rather than approximate: ROAS is continuous in the level, so a target between two
// readings of the same mix has a crossing between them for solveScale to find.
const MIX_SAMPLES = 41

// Warnings validateEkrmForm raises that are this wizard working as designed rather than anything a
// user could act on, and which are therefore not shown.
//
//   saturationPointBand — CURVE_PRIORITY deliberately places the grid where the channel saturates
//     rather than inside the band around the planned spend. It is the requirement, not a defect.
//   otherMissingAttributions / otherMissingTrends — a model report has no co-marketing attribution
//     and says nothing about future growth, so those cells are blank by construction in every
//     import a wizard makes.
export const EXPECTED_WARNING_CODES = [
  'saturationPointBand',
  'otherMissingAttributions',
  'otherMissingTrends'
]

export const SKIP_NO_SATURATION = 'noSaturation'
export const SKIP_NO_CURVE = 'noCurve'

// Why a report cannot be cloned at all. Each one is a hard error out of validateEkrmForm if it is
// allowed through, phrased there in English and pointing at a step this wizard does not have.
export const NOT_READY_NO_KPI = 'noKpi'
export const NOT_READY_NO_CHANNELS = 'noChannels'
export function sum (values) {
  return values.reduce(function (total, value) {
    const number = Number(value)
    return total + (isFinite(number) ? number : 0)
  }, 0)
}

export function mean (values) {
  if (!values || values.length === 0) return 0
  return sum(values) / values.length
}

// A multiplier the user typed, or the identity. Strictly positive: both of these are factors on a
// level and neither means anything at or below zero.
export function positiveFactor (value, fallback) {
  const number = Number(value)
  return isFinite(number) && number > 0 ? number : fallback
}

// Multiplying a row of cells re-rounds it, so the identity is returned untouched rather than passed
// through toCells — that is what keeps an unedited clone byte-identical to the one this wizard built
// before any of this existed.
function scaleCells (cells, factor) {
  if (!cells || factor === 1) return cells
  return toCells(cells.map(function (cell) { return Number(cell) * factor }))
}

// The three Hill parameters a curve edit moves, and the shape an edit is stored in: a RATIO over
// the parameters the import fitted, so `{ vmax: 1, k: 1, n: 1 }` — or nothing at all — is the
// untouched channel.
//
// Ratios and not absolute values, and that is the whole reason a drawing on a curve survives a
// period change and a change of unit rates. Both of those re-run the import, and both move the
// fitted numbers: a rate multiplies Vmax and K by their own axis's rate, and a window moves where
// the ten columns fall. An absolute ceiling typed against one import would mean something else
// against the next one; "a fifth higher than the model says" means the same thing against every
// import of the same model. It is the same reason mixFactors and the drawn year are stored the way
// they are, and it is cleared on the same event: another model, and nothing less.
export const CURVE_KEYS = ['vmax', 'k', 'n']

// The parameters an edited channel is read at, or null for one nobody has touched. Null and not the
// imported triple, because "untouched" has to be answerable without arithmetic: it is what keeps an
// unedited clone off this code path entirely and therefore byte-identical to the payload this wizard
// sent before any of this existed.
function editedHill (hill, edit) {
  if (!edit || !isHillParams(hill)) return null
  let moved = false
  const params = {}
  for (let i = 0; i < CURVE_KEYS.length; i++) {
    const key = CURVE_KEYS[i]
    const ratio = Number(edit[key])
    const factor = isFinite(ratio) && ratio > 0 ? ratio : 1
    if (factor !== 1) moved = true
    params[key] = hill[key] * factor
  }
  if (!moved) return null
  // A ratio that drove a parameter out of the function's own domain is not an edit the wizard can
  // send, and refusing it here leaves the channel exactly as it was imported.
  return isHillParams(params) ? params : null
}

// ---------------------------------------------------------------------------
// Units
// ---------------------------------------------------------------------------

// The two units the source model reports in: what its media spend is denominated in, and what the
// KPI being copied is counted in. Both live on the model's config rather than in the report, and an
// output that names its own sales unit overrides the model-wide one.
//
// 'USD' is the fallback for a config that names neither, which is what every model built here
// carries and what the rest of the app already assumes when the key is absent.
export function cloneUnits (model, kpiName) {
  const config = (model && model.reference_model && model.reference_model.config) || {}
  let output = config.sales_unit_name || EKRM_UNIT
  if (kpiName && kpiName !== 'Total' && config.outputs && config.outputs[kpiName]) {
    output = config.outputs[kpiName].sales_unit_name || EKRM_UNIT
  }
  return { spend: config.invest_unit_name || EKRM_UNIT, output: output }
}

// WHETHER A MODEL SPEAKS IN ONE UNIT — its media spend and its sales counted in the same money.
//
// The composer takes a whole model at once and asks what a unit of its money is worth, and a model
// with two different units is two exchange rates asked of somebody who came here to copy a model:
// two chances to state a pair that cannot both be right, and two numbers standing between the model
// picked and anything appearing on the page. So the copy list is the models that need at most one.
//
// Every output is asked and not only the model-wide unit, because an output may name its own — the
// KPI the copy lands on is the report's, which is not known until the report has been read, so a
// model where any of them disagrees is not offered at all rather than offered and then found out.
export function cloneUnitsAgree (model) {
  const config = (model && model.reference_model && model.reference_model.config) || {}
  const spend = config.invest_unit_name || EKRM_UNIT
  if ((config.sales_unit_name || EKRM_UNIT) !== spend) return false
  const outputs = config.outputs || {}
  return Object.keys(outputs).every(function (name) {
    const output = outputs[name] || {}
    return (output.sales_unit_name || EKRM_UNIT) === spend
  })
}

// The rates the wizard has to ask for before this model can be copied: one per DISTINCT unit name
// that is not already USD, each saying which of the two axes it converts.
//
// Keyed by the unit and not by the axis, because two axes carrying the same unit name carry the
// same unit — a model reporting EUR of spend against EUR of revenue has one exchange rate, not two,
// and asking twice would let a user enter a pair that cannot both be right. A model with two
// different non-USD units gets two entries; a USD/USD model gets none and nothing is asked at all.
export function cloneUnitConversions (units) {
  const source = units || {}
  const order = []
  const byUnit = {}
  const note = function (unit, axis) {
    if (!unit || unit === EKRM_UNIT) return
    if (!byUnit[unit]) {
      byUnit[unit] = { unit: unit, spend: false, output: false }
      order.push(byUnit[unit])
    }
    byUnit[unit][axis] = true
  }
  note(source.spend, 'spend')
  note(source.output, 'output')
  return order
}

// The two multipliers those rates come to, in the shape the import takes them: one for the spend
// axis, one for the response axis. An axis already in USD converts by 1.
//
// Null when any required rate is missing or is not a positive number. There is no sensible default
// to fall back on — 1 would mean "this EUR is a dollar" — so the wizard shows nothing and refuses
// to build until every rate it asked for has an answer.
export function cloneUnitRates (units, rates) {
  const source = units || {}
  const entered = rates || {}
  const rateFor = function (unit) {
    if (!unit || unit === EKRM_UNIT) return 1
    const value = Number(entered[unit])
    return isFinite(value) && value > 0 ? value : null
  }
  const spend = rateFor(source.spend)
  const revenue = rateFor(source.output)
  if (spend === null || revenue === null) return null
  return { spend: spend, revenue: revenue }
}

// Rates the model already carries, so a field can start somewhere rather than empty. The platform
// already knows both of these numbers for some models; it just has never had to state them together.
//
//   usd_per_unit / usd_per_unit_name   the RESPONSE axis. CreateCustomModel asks for exactly this
//     number — "USD per 1 unit" — whenever a brand's sales unit is not USD, prefilling it from the
//     brand and storing what the user settled on under custom_model. The stored one wins, being the
//     answer given for this model rather than the brand's default.
//
//   usd_per_marketing                  the SPEND axis. Never presented as a rate anywhere, but that
//     is what it is: the plan optimizer divides a country's minimum weekly spend, which is quoted in
//     USD, by it to get a minimum in the model's own spend units (src/mixins/settings.js). USD over
//     model-spend-units is USD per one unit of invest_unit_name.
//
// Keyed by the unit each figure was stored against rather than by the axis it came from, which is
// the same rule the fields themselves follow: a figure filed under a name is a statement about that
// unit wherever the unit turns up. So a model whose spend unit happens to be the one
// usd_per_unit_name records has that rate offered for its spend, and anything filed against a unit
// this model no longer reports in is not offered at all — the names are set in different places and
// a model can have moved on from the one its brand was configured with.
//
// A rate of exactly 1 is not offered. It is the default both call sites fall back to when nothing
// was configured, and for a unit that is not USD it is the one value that cannot be true.
export function cloneSuggestedRates (model, units) {
  const wanted = cloneUnitConversions(units)
  if (wanted.length === 0) return {}
  const config = (model && model.reference_model && model.reference_model.config) || {}
  const custom = (model && model.custom_model) || null
  const brands = config.brands || {}
  // The same order CreateOptimization resolves a brand in: the custom model names one, and anything
  // else has the first of the reference model's.
  const named = custom && custom.brand && brands[custom.brand] ? custom.brand : Object.keys(brands)[0]
  const brand = brands[named] || {}

  const suggested = {}
  const offer = function (unit, value) {
    if (!unit || unit === EKRM_UNIT || suggested[unit] !== undefined) return
    const rate = Number(value)
    if (!isFinite(rate) || rate <= 0 || rate === 1) return
    const asked = wanted.some(function (entry) { return entry.unit === unit })
    if (asked) suggested[unit] = rate
  }
  // In this order, so that when one unit answers to both figures the more specific one wins.
  offer(brand.usd_per_unit_name, custom && custom.usd_per_unit !== undefined ? custom.usd_per_unit : brand.usd_per_unit)
  offer(units ? units.spend : null, brand.usd_per_marketing)
  return suggested
}

// WHETHER A MODEL CAN BE COPIED WITHOUT ASKING WHAT ITS MONEY IS WORTH: it counts spend and sales in
// one unit, and that unit is either USD or one the model already carries a rate for. The composer
// does not ask for a rate, so a model it would have to ask about is not offered at all. The unit is
// read off the spend alone, which cloneUnitsAgree has already said every output shares, so the KPI
// the report lands on cannot change the answer.
export function cloneRatesKnown (model) {
  if (!cloneUnitsAgree(model)) return false
  const config = (model && model.reference_model && model.reference_model.config) || {}
  const unit = config.invest_unit_name || EKRM_UNIT
  const units = { spend: unit, output: unit }
  return cloneUnitRates(units, cloneSuggestedRates(model, units)) !== null
}

// Which KPI output a model is read at. The root of a v2 report is the Total, and a report without
// one has to name an output explicitly.
export function cloneKpiName (report) {
  if (report && report.v2) return 'Total'
  const names = kpiNames(report)
  return names.length > 0 ? names[0] : 'Total'
}

// One medium's cells written into the three grids that hold per-channel data, minus the
// reactivity a component's own import would need: the rows are built once and never replaced, so
// there is nothing to $set and no watcher to wait for.
//
// `decayEdit` is the composer's: { ranges, curve }, a redrawn kernel and the two brackets READ OFF
// it — see composeDecayPayload, which is where that reading is done and where it can be tested. It
// is optional in the sense that every caller that has not stated one gets exactly what it always
// got: the decay the source model measured, brackets and kernel both.
export function writeChannel (form, index, cells, spendFactor, curveEdit, decayEdit) {
  const medium = form.reference.media[index]

  // The spend multiplier lands here and nowhere else. One number per channel now, rather than one
  // for all of them: it is the common factor times whatever the mix solver gave this channel, and
  // for every clone that has not been through that solver the second half is 1. Everything below it — the decay, the
  // saturation header, the ten grid values and both curves — is the channel's response, which does
  // not move because the channel was given more or less money to spend. The variation range rides
  // through untouched for the same reason it survives the scale factor: it is a coefficient of
  // variation, and multiplying a series does not change its dispersion relative to its own mean.
  form.mediaSpend.ownControllable.data[index] =
    [medium, cells.spendVariationRange].concat(scaleCells(cells.spendCells, spendFactor))

  // THE DECAY, IN THE TWO PLACES THE PAYLOAD STATES ONE.
  //
  // A medium carries both: the brackets below, which every medium has, and — written by the
  // source-curve block at the foot of this function — the kernel itself, which the EKRM reads in
  // preference to them wherever there is one. A redrawn decay REPLACES both, exactly as a moved
  // saturation curve replaces its own points rather than dropping them, and for the same reason:
  // two blocks describing one channel must not end up describing two.
  //
  // Per MEDIUM and per BLOCK. The saturation curve beside it is untouched and so is every other
  // channel's decay — the same division the wizards make between a channel's own two halves.
  const decay = decayEdit ? decayEdit.ranges : cells.decay
  // The kernel a weekly source never sent is a kernel a redrawn one now does: what a user draws is
  // a claim about MONTHS, whatever axis the model that opened the editor was fitted on, and the
  // reason a weekly channel sends nothing is that a re-binning is not the convolution it would be
  // claiming to be. Nobody is claiming it here.
  const decayCurve = decayEdit ? decayEdit.curve : cells.decayCurve

  form.decay[medium] = decay
  form.minHalfLife[medium] = decay.maximumEffect[0]
  // A month past the peak is the slider's usual reach in the other wizard, opened up when the
  // half-life the report measured sits beyond it. Neither number reaches the definition — they ride
  // in originalDefinition as the slider bounds — but getting them wrong makes a clone reopened there
  // start with sliders that cannot reach their own values.
  form.maxHalfLife[medium] = Math.max(decay.maximumEffect[1] + 1, decay.halfLife[1])

  // Two rows per medium: the header carries the three numbers, the row below it the ten values.
  const header = form.saturation.data[2 * index]
  header[1] = cells.revenueVariationRange
  header[2] = cells.maximumSpendCell

  // The one edit here that moves a curve, and the reason it REPLACES the source points rather than
  // dropping them the way a hand-built form can: the backend's detection is all-or-nothing, so a
  // medium that stopped sending an x/y would take every other medium's curve down with it.
  //
  // The three expert cells are rewritten with it and not left standing beside it. The backend
  // ignores them for a medium that carries a curve, but check_ekrm_correctness reads them off the
  // raw payload and a grid still describing the imported response would be a second, contradictory
  // account of the same channel sitting in the same document.
  const edited = editedHill(cells.hill, curveEdit)
  const curve = edited ? hillCurvePoints(edited, cells.saturationCurve) : null
  if (edited && curve) {
    // Placed on the EDITED curve, by the same rule CURVE_PRIORITY placed it on the imported one:
    // far enough out that the ten values carry the ceiling. A knee dragged outward moves it.
    const pointCell = toCell(curveGridPoint(edited, Number(cells.saturationPointCell)))
    header[3] = pointCell
    form.saturation.data[2 * index + 1] =
      ['', '', '', ''].concat(toRevenueCells(hillSamples(edited, Number(pointCell))))
    form.curves[medium] = {
      vmax: edited.vmax,
      k: edited.k,
      n: edited.n,
      point: Number(pointCell),
      // Parametric by construction now, whatever the import made of the source curve. A channel
      // whose fit was rejected keeps its fitPct, which is what the editor warns with: for such a
      // channel moving a lever changes the SHAPE and not only the size.
      mode: 'hill',
      fitPct: cells.hillFitPct,
      source: { saturation: curve, decay: decayCurve }
    }
    return
  }

  header[3] = cells.saturationPointCell
  form.saturation.data[2 * index + 1] = ['', '', '', ''].concat(cells.revenueCells)

  // The source curves, which the EKRM fits directly instead of re-inferring them from the grid and
  // the two decay brackets. In a hand-built form either half stops travelling the moment the user
  // edits it; here only the saturation half is editable and an edit replaces it above, so both
  // always travel — which is the entire reason an imported channel reproduces its source where a
  // typed one can only approximate it. `decay` is null for a weekly model by design: its lag axis is in weeks and
  // the backend reads months.
  form.curves[medium] = {
    vmax: cells.hill.vmax,
    k: cells.hill.k,
    n: cells.hill.n,
    point: Number(cells.saturationPointCell),
    mode: cells.curveMode,
    fitPct: cells.hillFitPct,
    source: { saturation: cells.saturationCurve, decay: decayCurve }
  }
}

// Twelve numbers, or nothing. A seasonality is a year, and this is the one place a candidate for one
// — the user's drawing, or the import's own cells — is checked for being one. Blanks and anything
// unreadable become zero rather than dropping a month, the row having to keep its width.
export function seasonalityYear (values) {
  if (!Array.isArray(values) || values.length !== 12) return null
  return values.map(function (value) {
    const number = Number(value)
    return isFinite(number) ? number : 0
  })
}

// That year laid across the window, by CALENDAR MONTH and not by position, so a window that does not
// begin in January still lands each value on its own month. `months` is null for the averaged year,
// whose twelve columns are January to December by construction.
//
// toCells re-rounds, and that is safe in both directions: a year read back off a row has already been
// through it, and toCell is idempotent on two decimals — which is what keeps a clone nobody has drawn
// on byte-identical to the payload this wizard sent before.
function spreadYear (year, months) {
  if (!months || months.length === 0) return toCells(year)
  return toCells(months.map(function (key) {
    const month = Number(String(key).slice(5, 7)) - 1
    return month >= 0 && month < 12 ? year[month] : 0
  }))
}

// The year the source model itself sells in: every January averaged against every January, as a
// percentage of that average January's revenue. What the editor opens on.
//
// The window is CALENDAR_AVERAGE and never the user's, on purpose — a seasonality is one year, and
// the wizard shows the model's own rather than whichever slice of history the period picker is
// standing on. It takes no unit rates either: seasonalityVector divides an effect by the KPI it was
// measured against, so the ratio is the same in any currency. Both together are what let this
// survive a period change and a rate change and be reset only when another model is picked.
//
// Null, never twelve zeros, for a source that carried no seasonality series at all.
export function cloneSeasonalityPattern (scope) {
  if (!scope) return null
  const cells = importGlobalCells(scope, { mode: CLONE_MODE, months: null })
  return seasonalityYear(cells.seasonalityCells)
}

// The three derived attribution rows. Written last, and not alongside the two above, because
// derivedAttributionRows weights the periodic and spot vectors by the KPI row and closes Base against
// the first three attribution rows — run any earlier it would weight an empty grid. A wizard that
// builds the form a step at a time reaches the same call through separate watchers.
export function writeDerivedAttributions (form) {
  const derived = derivedAttributionRows(form)
  form.attributions.data[3][1] = derived.periodic.min
  form.attributions.data[3][2] = derived.periodic.max
  form.attributions.data[4][1] = derived.spot.min
  form.attributions.data[4][2] = derived.spot.max
  form.attributions.data[5][1] = derived.base.min
  form.attributions.data[5][2] = derived.base.max
}

// Base, closed against the other five as they now stand. derivedAttributionRows does this too, from
// the rows IT derived; this reads the rows the form is actually carrying, which is what the periodic
// band below leaves behind. Same arithmetic, deliberately: crossed ends, both clamped.
//
// With one guard that function does not have, and it is not a refinement — it is the difference
// between a payload and a hard validator error. The two ends are crossed, so before clamping they
// are ordered by construction: every row's max is at least its min, so 100 - sumMax is at most
// 100 - sumMin. The CLAMPS are what can break that, because they are applied to different sides —
// and they do break it exactly when the other components net NEGATIVE, which a competition
// attribution (always negative, by definition) and a marketing band that has rounded to nothing are
// enough to produce. Base then comes out at 103/100 and `validateEkrmForm` refuses the payload for a
// row nobody typed.
//
// The wide periodic band an import used to send was hiding it: twelve points of it on either side
// swamped the sum and kept it positive. Ordering the pair afterwards is the smallest honest fix,
// and it is made here rather than in derivedAttributionRows because the typed wizard shares that
// one.
export function closeBase (form) {
  const rows = form.attributions.data
  let sumMin = 0
  let sumMax = 0
  for (let index = 0; index < 5; index++) {
    sumMin += Number(rows[index][1])
    sumMax += Number(rows[index][2])
  }
  if (isNaN(sumMin) || isNaN(sumMax)) {
    rows[5][1] = '0'
    rows[5][2] = '0'
    return
  }
  const low = 100 - Math.min(100, sumMax)
  const high = 100 - Math.max(0, sumMin)
  rows[5][1] = Math.round(Math.min(low, high)).toString()
  rows[5][2] = Math.round(Math.max(low, high)).toString()
}

// The periodic attribution band, written the way the marketing one is: over the row
// derivedAttributionRows just left, at two decimals, and with Base closed against it afterwards.
//
// It is written here rather than left to derivedAttributionRows for two reasons, and the second is
// the one that matters. The first is resolution — that function rounds both ends to whole percentage
// points, and the whole of a seasonality usually lives inside one. The second is width: it spreads
// the band by the row's own variation range, which is the model's MAPE clamped to [5, 40], read as
// POINTS OF SALES when everywhere else in the pipeline a variation range is a relative percentage.
// A seasonality of 2% therefore travelled declared as "between -10% and +14%".
//
// That is not a constraint, and the EKRM has nothing else to go on. `adjust_attributions` rewrites
// the periodic scale of the model it builds whenever the fitted attribution falls OUTSIDE this band
// — periodic is not exempt, only marketing is, and only when its curves were given — but it does
// nothing at all while the attribution is inside. The row's own variation range is read by the
// builder and never used, and the prior on the periodic scale is hard-coded. So this band is the
// only thing standing between what the user drew and what the model comes back with, and a wide one
// is why a built clone's seasonality overshot its own payload and left the noise making up the
// difference.
//
// derivedAttributionRows itself is left alone: the typed wizard shares it, and there the row is
// typed by an expert who is entitled to their own width.
export function writeSeasonalityBand (form) {
  const context = seasonalityContext(form)
  if (!context) return null
  const row = form.seasonalityAndSpotEvents.data[0]
  const shares = seasonalityShares(row, context)
  if (!isFinite(shares.centre) || !isFinite(shares.size)) return null
  const written = seasonalityBand(shares)
  form.attributions.data[3] = [form.attributions.data[3][0], String(written.min), String(written.max)]
  return shares.centre
}

// The Total ROAS the EKRM will report once it is built, by running the payload the way the EKRM will
// run it: every medium's saturation curve evaluated at every one of its twelve monthly spends, summed,
// over the spend that bought it.
//
// It does NOT use the Marketing Attribution band, which is the obvious thing to do and is wrong. The
// band is what the source model measured; the EKRM is not held to it. Measured against one built
// model, its own attribution came out at 1.95 % where the band said 4.08–4.33 %, because a saturation
// curve is convex at low spend and the EKRM is a monthly model fed calendar-averaged months: money a
// weekly source spent in bursts is spread thin here, and thin money on a convex curve returns much
// less. The curves are the payload's own account of that, so reading them back is the only estimate
// that moves when the payload does.
//
// It is an estimate, and the box says so. On that same model this sum ran ~12 % above what the built
// EKRM reported (0.105 against 0.093): the backend refits its own curve through these points and its
// decay carries part of each month's response past the end of the year, where a twelve-month total
// loses it. Both effects are small and both push the same way. One built model is one data point, so
// no correction factor is applied to a bias measured that thinly.
//
// Null, never zero, when there is no spend or a medium has no curve of any kind: a missing curve is
// silence, and summing it as zero would understate the whole figure without saying so.
export function cloneRoas (form) {
  const spendTotal = cloneSpendTotal(form)
  if (!(spendTotal > 0)) return null
  const marketing = cloneMarketing(form)
  if (marketing === null) return null
  return marketing / spendTotal
}

// The twelve-month media spend the form carries, over the own-controllable channels — the ones that
// become media of the EKRM.
export function cloneSpendTotal (form) {
  if (!form) return 0
  const media = (form.reference && form.reference.media) || []
  let total = 0
  for (let index = 0; index < media.length; index++) {
    const row = (form.mediaSpend.ownControllable.data || [])[index] || []
    total += sum(row.slice(2))
  }
  return total
}

// The revenue those curves say that spend will return: every medium's curve at every one of its
// twelve months, summed. The numerator of cloneRoas, factored out because the marketing attribution
// the payload implies is the same number over the KPI instead of over the spend, and the two should
// not be able to disagree.
//
// Null, never zero, when a medium has no curve of any kind — see cloneRoas.
export function cloneMarketing (form) {
  if (!form) return null
  const media = (form.reference && form.reference.media) || []
  if (media.length === 0) return null

  let response = 0
  for (let index = 0; index < media.length; index++) {
    const row = (form.mediaSpend.ownControllable.data || [])[index] || []
    const cells = row.slice(2).map(Number)
    const curve = mediumResponseCurve(form, index, media[index])
    if (!curve) return null
    for (let month = 0; month < cells.length; month++) {
      response += responseAt(curve, cells[month])
    }
  }
  return response
}

// THE WHOLE READING, KEPT AS IT IS TAKEN: what every medium's curve returns in every one of its
// months, one row per medium and one column per month. cloneMarketing is its total,
// cloneMarketingMonthly its column sums and cloneMarketingByMedium its row sums — all three are
// margins of this one matrix, and they are written as margins of it below rather than as three
// traversals that each read the curves again. Three readings of one thing is three places for it to
// drift, and every chart in the summary is a different margin of the same attribution: a media mix
// whose columns did not add up to the waterfall's Marketing bar would be the report disagreeing with
// itself inside one screen.
//
// `factor` is a common multiplier over every cell, which is what the spend slider moves while it is
// being dragged — the form does not carry that level yet — and defaults to the form as it stands.
//
// Null, never a short list and never a row of zeros, when a medium has no curve of any kind — see
// cloneMarketing. Every margin inherits that silence, which is what makes a missing curve a gap in
// each of these charts rather than a channel drawn at nothing.
export function cloneMarketingMonthlyByMedium (form, factor) {
  if (!form) return null
  const media = (form.reference && form.reference.media) || []
  if (media.length === 0) return null
  const scale = (factor === undefined || factor === null) ? 1 : Number(factor)
  if (!isFinite(scale) || scale < 0) return null

  const rows = []
  for (let index = 0; index < media.length; index++) {
    const row = (form.mediaSpend.ownControllable.data || [])[index] || []
    const cells = row.slice(2).map(function (cell) {
      const value = Number(cell)
      return isFinite(value) ? value : 0
    })
    const curve = mediumResponseCurve(form, index, media[index])
    if (!curve) return null
    const monthly = []
    for (let month = 0; month < cells.length; month++) {
      monthly.push(responseAt(curve, cells[month] * scale))
    }
    rows.push({ medium: media[index], monthly: monthly })
  }
  return rows
}

// The same reading as cloneMarketing, month by month rather than as one total: the two revenue boxes
// draw a bar per month and the stacked one has to split each month at the same place the headline
// figures split the year. The column sums of the matrix above.
//
// Summing this is cloneMarketing exactly, at a factor of one, and there is a test that says so: the
// stack's segments and the figure above them come from the same curves read the same way, and a
// second traversal that drifted from the first would be a stack that does not add up to its own
// headline.
//
// Null, never a row of zeros, when a medium has no curve of any kind — see cloneMarketing.
export function cloneMarketingMonthly (form, factor) {
  const rows = cloneMarketingMonthlyByMedium(form, factor)
  if (rows === null) return null

  const monthly = []
  for (let index = 0; index < rows.length; index++) {
    const values = rows[index].monthly
    for (let month = 0; month < values.length; month++) {
      while (monthly.length <= month) monthly.push(0)
      monthly[month] += values[month]
    }
  }
  return monthly
}

// The same reading again, kept per medium instead of summed or kept per month: the sales
// decomposition beside the spend chart is one bar per channel, and each bar is what THAT channel's
// curve returns over its own twelve months. The row sums of the matrix above.
//
// Summing the values is cloneMarketing exactly at a factor of one, and there is a test that says so
// — for the reason cloneMarketingMonthly has one. A waterfall whose bars come from a traversal that
// has drifted from the one behind the total is a waterfall that does not land on the figure written
// over it.
//
// Null, never a short list, when a medium has no curve of any kind — see cloneMarketing.
export function cloneMarketingByMedium (form, factor) {
  const rows = cloneMarketingMonthlyByMedium(form, factor)
  if (rows === null) return null
  return rows.map(function (row) {
    let response = 0
    for (let month = 0; month < row.monthly.length; month++) response += row.monthly[month]
    return { medium: row.medium, response: response }
  })
}

// What the source model's seasonality is credited with, as a signed amount of this revenue: row 0 of
// the seasonality grid — a percentage of each month's KPI — dotted with the KPI row itself.
//
// The percentage IS the share of total sales, and that is the payload's own arithmetic rather than
// an approximation of it. The build divides the row by 100 and then rescales it to p / (1 - p - o)
// before attaching it as an additive factor over everything that is not seasonal; a factor of
// p / (1 - p) over the non-seasonal part makes the total that part over (1 - p), so what the factor
// contributes is exactly p of the total. Sending "8" means eight percent of the revenue, at every
// month, by construction.
//
// The KPI row is passed in rather than read off the form because the revenue multiplier moves it and
// this row does not: seasonality is already a ratio, so it rides that multiplier for free and a
// caller holding a scaled KPI vector — which is what the revenue slider produces mid-drag — gets the
// seasonality at that same level without the form having been rebuilt.
//
// Null, never zero, when the source model carried no seasonality series at all: writeGlobals leaves
// the row blank in that case, and a blank row is silence rather than a model with no seasonality.
//
// `rowIndex` picks the row, 0 the periodic one and 1 the spot events, and defaults to the periodic
// row. The clone wizard writes only that one, so a caller there gets what it always got; the
// composer writes both and every base rule downstream has to subtract both.
export function cloneSeasonality (form, kpiVector, rowIndex) {
  if (!form || !form.seasonalityAndSpotEvents) return null
  const row = (form.seasonalityAndSpotEvents.data || [])[rowIndex || 0]
  if (!row || !Array.isArray(kpiVector)) return null
  const shares = row.slice(2)
  let anyValue = false
  let total = 0
  for (let month = 0; month < kpiVector.length; month++) {
    const cell = shares[month]
    if (cell === '' || cell === null || cell === undefined) continue
    const share = Number(cell)
    const kpi = Number(kpiVector[month])
    if (!isFinite(share) || !isFinite(kpi)) continue
    if (share !== 0) anyValue = true
    total += kpi * share / 100
  }
  return anyValue ? total : null
}

// The marketing attribution this payload implies: what the curves return at this spend, as a share
// of this revenue. The figure `adjust_attributions` compares against the band on the way in, and
// therefore the figure that decides whether the EKRM's saturation asymptotes are rescaled.
export function cloneAttribution (form) {
  const marketing = cloneMarketing(form)
  if (marketing === null || !form) return null
  const kpi = sum((form.kpi.data[0] || []).slice(2))
  if (!(kpi > 0)) return null
  return 100 * marketing / kpi
}

// The least Base Revenue a reading of the curves leaves room for.
//
// Base Revenue is the revenue less what the curves are credited with, and that reading does not move
// when the revenue does: revenueFactor multiplies the KPI row and never reaches a spend cell or a
// curve. So there is a revenue below which the base is negative — the media returning more than the
// whole of what was sold, which is not a model of anything — and this is the floor above it.
//
// MAX_ATTRIBUTION_SHARE is the same ceiling writeMarketingBand clamps the band at, applied to the
// reading rather than to the band: the most of a revenue that marketing may be credited with is one
// number, whichever of the two is saying it. What is left of the revenue at that ceiling is the
// floor, so this is a function of the curves and the spend ALONE. Nothing the user does to the
// revenue moves it — which is exactly why it can bound the revenue.
//
// Null, never zero, for a reading there is none of — the silence cloneMarketing keeps.
export function cloneBaseFloor (response) {
  const marketing = Number(response)
  if (!isFinite(marketing) || !(marketing > 0)) return null
  return marketing * (100 - MAX_ATTRIBUTION_SHARE) / MAX_ATTRIBUTION_SHARE
}

// The media as every reading below sees them: each medium's monthly spend as the form carries it
// now, and the curve the EKRM will read that spend off. Pulled out once and passed around, because
// the solvers evaluate a few hundred plans over the same channels and re-reading the grid for each
// one is the whole cost.
//
// Null, never a short list, when a medium has no curve of any kind — the same silence cloneRoas
// keeps rather than summing a missing curve as zero.
function roasChannels (form) {
  const media = (form && form.reference && form.reference.media) || []
  if (media.length === 0) return null
  const channels = []
  for (let index = 0; index < media.length; index++) {
    const row = ((form.mediaSpend.ownControllable.data || [])[index] || []).slice(2)
    const cells = row.map(function (cell) {
      const value = Number(cell)
      return isFinite(value) ? value : 0
    })
    const curve = mediumResponseCurve(form, index, media[index])
    if (!curve) return null
    channels.push({ cells: cells, curve: curve, total: sum(cells) })
  }
  return channels
}

// What the media would spend and what the curves say it would return at a mix and a level: every
// channel's spend multiplied by its own weight and then by one common scale. `weights` of null is
// the mix the form already carries, which is what the Media Spend box and the ROAS box's own solve
// move — one number over all of them.
function readingAt (channels, weights, scale) {
  let spendTotal = 0
  let response = 0
  for (let index = 0; index < channels.length; index++) {
    const channel = channels[index]
    const factor = (weights ? weights[index] : 1) * scale
    for (let month = 0; month < channel.cells.length; month++) {
      const spend = channel.cells[month] * factor
      spendTotal += spend
      response += responseAt(channel.curve, spend)
    }
  }
  if (!(spendTotal > 0)) return null
  return { spend: spendTotal, response: response, roas: response / spendTotal }
}

// The same reading as one number, which is all the solvers want.
function roasAt (channels, weights, scale) {
  const reading = readingAt(channels, weights, scale)
  return reading === null ? null : reading.roas
}

// The common multiplier on a mix that makes it report `targetRoas`, searched inside `bracket` or,
// with none, the whole of the one the Media Spend box has always had.
//
// Sampled across the whole bracket before it bisects, and it keeps the answer CLOSEST TO NO CHANGE
// rather than the first one it meets, because ROAS against spend is not monotone and usually has two
// answers. A Hill curve with an exponent above one is convex below its knee, so its average return
// climbs from nothing, peaks, and falls; a channel already past that peak can be given the return
// the user asked for either by easing back towards the peak or by cutting the spend to almost
// nothing on the far side of it. The near one is the one they meant.
//
// Null when no crossing exists at all.
function solveScale (channels, weights, targetRoas, bracket) {
  const target = Number(targetRoas)
  if (!channels || !isFinite(target) || target <= 0) return null

  const logMin = Math.log(bracket ? bracket.min : SOLVE_MIN_FACTOR)
  const logMax = Math.log(bracket ? bracket.max : SOLVE_MAX_FACTOR)
  const step = (logMax - logMin) / (SOLVE_SAMPLES - 1)

  let best = null
  let lowLog = logMin
  let low = roasAt(channels, weights, Math.exp(logMin))
  if (low === null) return null

  for (let sample = 1; sample < SOLVE_SAMPLES; sample++) {
    const highLog = logMin + sample * step
    const high = roasAt(channels, weights, Math.exp(highLog))
    if (high === null) return null
    if ((low - target) * (high - target) <= 0) {
      let left = lowLog
      let leftValue = low
      let right = highLog
      for (let pass = 0; pass < SOLVE_ITERATIONS; pass++) {
        const middle = (left + right) / 2
        const value = roasAt(channels, weights, Math.exp(middle))
        if (value === null) return null
        if ((leftValue - target) * (value - target) <= 0) {
          right = middle
        } else {
          left = middle
          leftValue = value
        }
      }
      const root = (left + right) / 2
      // Nearest to a factor of one, which on a log axis is nearest to zero.
      if (best === null || Math.abs(root) < Math.abs(best)) best = root
    }
    lowLog = highLog
    low = high
  }
  return best === null ? null : Math.exp(best)
}

// The best and the worst Total ROAS a mix can be made to report, over the bracket solveScale
// searches — and the multiplier each of those two readings was taken at, so a caller that has to
// PUT the form on one of them has somewhere to put it. The ends are where a solver finds nothing:
// asked for the very best a mix can do, solveScale is looking for a crossing at a turning point and
// bisects around a root it never quite lands on, and the low end sits on the frame of the bracket
// rather than on a crossing at all. Both are read straight off the sampling instead, which is exact
// to the step: a level sampled here is a level the curve was actually read at.
//
// `minFactor` and `maxFactor` are multipliers on the spend the form carries now, the way every
// solver in this file answers, and for a reach taken over WEIGHTS they are multipliers on that
// weighted mix rather than on the form's own — see cloneMixReach, whose caller reports the range
// and does not stand the form on it.
function reachOf (channels, weights, bracket, sampleCount) {
  if (!channels) return null
  const samples = sampleCount || SOLVE_SAMPLES
  const logMin = Math.log(bracket ? bracket.min : SOLVE_MIN_FACTOR)
  const step = (Math.log(bracket ? bracket.max : SOLVE_MAX_FACTOR) - logMin) / (samples - 1)
  let min = null
  let max = null
  let minFactor = null
  let maxFactor = null
  for (let sample = 0; sample < samples; sample++) {
    const factor = Math.exp(logMin + sample * step)
    const value = roasAt(channels, weights, factor)
    if (value === null) return null
    if (min === null || value < min) { min = value; minFactor = factor }
    if (max === null || value > max) { max = value; maxFactor = factor }
  }
  return min === null ? null : { min: min, max: max, minFactor: minFactor, maxFactor: maxFactor }
}

// The common multiplier on every channel's spend that makes this form report `targetRoas`.
//
// A multiplier and not a spend, so the mix the form carries survives being asked for a different
// return. Returned rather than applied: the caller multiplies its own factors by it and rebuilds,
// which is the only path that keeps the form and the factors in step.
//
// Null when no crossing exists at all: cloneRoasReach says what the curves can do at this mix, and
// solveSpendMix what they could do at another.
export function solveSpendFactor (form, targetRoas) {
  return solveScale(roasChannels(form), null, targetRoas)
}

// The best and the worst Total ROAS these curves can be made to report AT THE MIX THE FORM CARRIES.
// What the wizard tells the user when they ask for one that mix cannot reach.
export function cloneRoasReach (form) {
  return reachOf(roasChannels(form), null)
}

// What this form would spend and what it would return with every channel's spend multiplied by
// `factor`. A reading at a level the form does not carry yet, so a figure can follow a slider
// without the form being rebuilt for every pixel of it. Null when there is no reading to take.
export function cloneRoasAt (form, factor) {
  const scale = Number(factor)
  const channels = roasChannels(form)
  if (!channels || !isFinite(scale) || scale <= 0) return null
  return readingAt(channels, null, scale)
}

// The trade-off those two figures are two readings of: the Total ROAS at every level of media spend,
// at the mix the form carries. Drawn under them, which is the one place the shape of it can be seen
// — that it climbs, peaks and falls is why a return can be out of reach, why two spends can return
// the same, and why the wizard solves rather than divides.
//
// `options.from` and `.to` are multipliers on the form's own spend and default to a tenth and ten;
// `.samples` is how many readings across them, logarithmically, because the interesting range spans
// orders of magnitude. Null when the form carries no curves to read.
export function cloneRoasCurve (form, options) {
  const opts = options || {}
  const channels = roasChannels(form)
  if (!channels) return null
  const from = opts.from > 0 ? opts.from : 0.1
  const to = opts.to > from ? opts.to : from * 100
  const samples = opts.samples > 1 ? opts.samples : SOLVE_SAMPLES
  const logFrom = Math.log(from)
  const step = (Math.log(to) - logFrom) / (samples - 1)

  const points = []
  for (let sample = 0; sample < samples; sample++) {
    const factor = Math.exp(logFrom + sample * step)
    const reading = readingAt(channels, null, factor)
    if (reading === null) continue
    points.push({ factor: factor, spend: reading.spend, roas: reading.roas })
  }
  return points.length > 1 ? points : null
}

// Why a media spend is as far as it goes: the marketing attribution the payload would carry, or the
// revenue it is supposed to have produced.
export const LIMIT_ATTRIBUTION = 'attribution'
export const LIMIT_BASE = 'base'

// And why a CURVE is: the same two, plus the one rule that is about the curve rather than about the
// plan around it — its own two figures fallen under the least the caller states a figure at. See
// curveBreaks.
export const LIMIT_FLOOR = 'floor'

// How far inside the revenue the high end stands. The warning it marks fires at EQUALITY — the
// validator wants the revenue larger than the spend, not equal to it — and the cells are re-rounded
// on the way into the form, so a limit sitting exactly on the revenue is one the slider can land on
// and trip. A tenth of a percent under is inside every rounding involved and invisible on the chart.
export const LIMIT_MARGIN = 0.999

// The first multiplier in the bracket at which `ok` becomes true, for an `ok` that is false below it
// and true above. Null when it is true from the start — nothing binds — and null when it is false all
// the way to the top, which is a limit outside anything the wizard can reach.
function firstFactor (ok) {
  const logMin = Math.log(SOLVE_MIN_FACTOR)
  const logMax = Math.log(SOLVE_MAX_FACTOR)
  if (ok(SOLVE_MIN_FACTOR)) return null
  if (!ok(SOLVE_MAX_FACTOR)) return null
  let low = logMin
  let high = logMax
  for (let pass = 0; pass < SOLVE_ITERATIONS; pass++) {
    const middle = (low + high) / 2
    if (ok(Math.exp(middle))) high = middle
    else low = middle
  }
  return Math.exp(high)
}

// THE ENDS OF THE SPEND CORRIDOR THAT MENTION NO REVENUE — and the whole of it for a wizard that
// has not been given one yet.
//
// Two of the five ends a spend can run into are statements about the media ALONE: a channel's own
// twelve cells rounding away to nothing, and the media returning less between them than a month of
// media costs. Both are low ends. The other three — the attribution wall beneath, and the
// attribution ceiling, the revenue wall and the base wall above — are each read against the revenue
// the form is carrying, and have nothing to say about a form carrying none.
//
// So this is the corridor with the revenue left FREE: a floor, and no ceiling at all. Nothing about
// the media bounds a spend from above once the revenue is allowed to be whatever that spend
// produced — the three ends that do are the three that name it.
//
// It is what the composer's Media Spend card is held inside before a revenue has been typed. That
// wizard asks for the media, then for what is spent on them, then for the revenue that spend
// produced, so the spend is its LEAD figure and is answered against the curves alone. seatRevenue
// there is the other half of the pair — with the spend leading it is the REVENUE that is re-seated
// when the two drift apart, which is seatSpend's argument read in the mirror.
//
// `options.floor` is the least a figure may be stated at in the wizard asking. Both ends below are
// read off it, so a caller that states none is handed a corridor with no floor either — which is
// the clone, where the spend is never the lead figure and never asks this on its own.
export function cloneSpendFloorLimits (form, options) {
  const channels = roasChannels(form)
  if (!channels) return null
  const spendNow = cloneSpendTotal(form)
  if (!(spendNow > 0)) return null
  return { min: spendFloorEnd(channels, spendNow, Number((options || {}).floor)), max: null }
}

// The higher of those two floors, as a multiple of the spend the form carries now, and null where
// neither binds.
//
// THE FIRST is the spend at which a medium's own row stops saying anything. Every cell is written
// through toCell, so a plan whose largest month for some channel is under the floor reaches the
// build as a row of zeros — which validateEkrmForm refuses per MEDIUM and not over the total, a
// budget spent entirely on one channel being no answer for the ones it left behind.
//
// Usually inside the attribution wall and occasionally not: the two are different questions. The
// attribution asks what the media are credited with over the whole revenue, so a plan with one
// enormous channel and one tiny one can pass it with room to spare while the tiny one's twelve
// months have already rounded away.
//
// THE SECOND is about what the money BUYS rather than about what is spent: the level below which
// the media return less than the floor a month between them.
//
// It was put here for a failure that no longer happens, and it stays because it is a true statement
// either way. The composer used to TRACK the revenue off the media until the box was typed over —
// it opened at the size that left them explaining a tenth of it — so the centre was pinned at that
// tenth however far the spend fell, the attribution wall never bound twice, and each re-seating
// handed the next drag the same room again. Three passes of that left curves credited with a
// millionth of what they were. That wizard's revenue box now opens EMPTY and is never written by
// anything but the user, so the centre no longer follows the spend down and the wall binds properly
// on its own.
//
// What is left is the plain claim, which was always the honest reading of it: a month of media that
// returns less than a month of media costs at its own floor is a plan that has stopped saying
// anything, whatever the revenue beside it is doing — or whether there is one at all, which is what
// lets this pair stand as a corridor of their own.
//
// Read over the year the rows carry, so it is the same statement as the cell floor above.
function spendFloorEnd (channels, spendNow, stated) {
  if (!(isFinite(stated) && stated > 0)) return null
  const at = function (factor, reason) {
    return factor === null ? null : { factor: factor, spend: factor * spendNow, reason: reason }
  }
  let cells = null
  let months = 0
  for (let index = 0; index < channels.length; index++) {
    const row = channels[index].cells
    const most = row.length > 0 ? Math.max.apply(null, row) : 0
    months = Math.max(months, row.length)
    if (!(most > 0)) continue
    const ratio = stated / most
    if (cells === null || ratio > cells) cells = ratio
  }
  let low = at(cells, LIMIT_FLOOR)

  const returned = months > 0 ? firstFactor(function (factor) {
    const reading = readingAt(channels, null, factor)
    return reading !== null && reading.response >= LIMIT_MARGIN * stated * months
  }) : null
  const earning = at(returned === null ? null : returned / LIMIT_MARGIN, LIMIT_FLOOR)
  if (earning && (!low || earning.factor > low.factor)) low = earning
  return low
}

// The smallest band centre `band` still writes a positive minimum for — the wall the revenue's high
// end and the spend's low end are both standing against, read off `band` itself rather than restated
// as the 0.065 it comes out at. The width, the floor under it and the two decimals a cell holds are
// all that function's own business, and a constant copied out of it here is one that goes stale the
// moment any of the three moves.
//
// Exported for the composer, whose Total Revenue box stands against this same wall from the other
// side: there the revenue is the denominator of the attribution, so it is the revenue that has a
// ceiling rather than the spend that has a floor. See composeRevenueCeiling.
//
// Bisected because it is a wall and not a formula: below it the minimum rounds to nothing or to a
// negative, above it the payload carries a marketing attribution validateEkrmForm will accept.
export function leastAttributionCentre () {
  let low = 1e-4
  let high = 1e2
  if (!(band(high).min > 0) || band(low).min > 0) return null
  for (let pass = 0; pass < SOLVE_ITERATIONS; pass++) {
    const middle = (low + high) / 2
    if (band(middle).min > 0) high = middle
    else low = middle
  }
  return high
}

// The three rules EITHER row of the seasonality grid has to satisfy, asked of a candidate row rather
// than of the form the row belongs to — which is what lets the amplitude rail, a single typed month
// and both rows be bounded by the same arithmetic instead of by four restatements of it.
//
// `rowIndex` says which row is moving: 0 the periodic one, 1 the spot events. Every rule below is
// symmetric in the two — a month's hundred percent is shared between them, the attribution sum
// counts whichever band is standing, and the base has both subtracted from it — so the only thing
// the index changes is which is the candidate and which is the neighbour. It defaults to the
// periodic row, that being the only row the clone wizard writes.
//
// Everything is read off the row as it will be WRITTEN, two decimals wide and with the other row
// added in, so none of these can drift from validateEkrmForm by a rounding.
//
//   month        a month credited with more of this component than the whole of its own revenue,
//                alone or once the other row's claim on the same month is added. seasonalityVector
//                clamps the imported row into ±100 before anything touches it, so this wall exists
//                for every model.
//   attribution  the attribution rows no longer closing on a hundred. Two hard errors and one wall:
//                upward this row is counted against the same hundred as the marketing band, and
//                downward — for a year whose KPI-weighted share is negative — it drags the sum of
//                the maxima below zero, at which point Base is clamped to a minimum above its own
//                maximum and the validator refuses a row nobody typed.
//   base         the two rows and the curves together credited with more revenue than there is:
//                cloneBaseFloor, the floor the media spend is already held above, asked of the other
//                side of the same subtraction.
//
// `context` is everything that does NOT move while a row is being tried — gathered once by the
// caller so a bisection does not re-read the curves sixty times.
function seasonalityContext (form, rowIndex) {
  if (!form || !form.seasonalityAndSpotEvents || !form.kpi || !form.attributions) return null
  const which = rowIndex === 1 ? 1 : 0
  const rows = form.seasonalityAndSpotEvents.data || []
  const row = rows[which]
  const attributions = form.attributions.data || []
  if (!row || attributions.length < 5) return null
  const kpi = (form.kpi.data[0] || []).slice(2).map(Number)
  const total = sum(kpi)
  if (!(total > 0)) return null
  const marketing = cloneMarketing(form)

  // THE OTHER ROW OF THE SAME GRID, held still while this one is tried. Which one that is depends on
  // which one is moving, and both directions are real now: the composer draws a periodic year with a
  // spot year already under it, and a spot year with a periodic one already above it. It is blank in
  // the clone wizard, which writes only the periodic row — every term below then reads zero and this
  // whole generalisation costs it nothing.
  const other = rows[1 - which] || []

  // What that row is credited with, in money, so the base rule can subtract BOTH. Read here rather
  // than inside the rule because it does not move while this row is being bisected sixty times.
  let otherNet = 0
  for (let month = 0; month < kpi.length; month++) {
    const cell = other[month + 2]
    if (cell === '' || cell === null || cell === undefined) continue
    const share = Number(cell)
    const size = Number(kpi[month])
    if (!isFinite(share) || !isFinite(size)) continue
    otherNet += size * share / 100
  }

  return {
    label: row[0],
    range: row[1],
    row: which,
    other: other,
    otherNet: otherNet,
    // Where the two rows' bands land in the attribution grid — this row's and its neighbour's. The
    // periodic row is row 3 and the spot row is row 4, and the rule below needs both: the one this
    // row will be written into, which the candidate replaces, and the one beside it, which stands.
    otherAttribution: which === 0 ? 4 : 3,
    kpi: kpi,
    total: total,
    attributions: attributions,
    marketing: marketing,
    floor: cloneBaseFloor(marketing)
  }
}

// The share of sales a candidate row declares, and the size that share is measured against. The size
// is the AMPLITUDE and not the share itself, for the reason cloneSeasonalityAmplitude exists: over
// any whole number of calendar years the signed share nets to nearly nothing, and a band that is 3%
// of nearly nothing is a band nothing can be held inside.
function seasonalityShares (row, context) {
  let net = 0
  let amplitude = 0
  for (let month = 0; month < context.kpi.length; month++) {
    const cell = row[month + 2]
    if (cell === '' || cell === null || cell === undefined) continue
    const share = Number(cell)
    const kpi = Number(context.kpi[month])
    if (!isFinite(share) || !isFinite(kpi)) continue
    net += kpi * share / 100
    amplitude += Math.abs(kpi * share / 100)
  }
  return { net: net, centre: 100 * net / context.total, size: 100 * amplitude / context.total }
}

// The band that share travels in. Same shape as `band` — plus or minus 3% with a floor of 0.12
// points across — measured against the amplitude rather than against the net.
//
// This is the row that actually binds. `adjust_attributions` rewrites the EKRM's periodic scale
// whenever the model it builds lands outside it, and it is the ONLY thing that does: the row's own
// variation range is read by the builder and never used, and the prior on the periodic scale is
// hard-coded. Left at the width derivedAttributionRows gives it — the KPI's own variation range,
// read as points of sales rather than as the relative percentage it is everywhere else in the
// pipeline — a seasonality of 2% travels declared as "between -10% and +14%", which is not a
// constraint, and the fitted seasonality lands wherever it likes with the noise making up the
// difference.
function seasonalityBand (shares) {
  const round = function (value) { return Math.round(value * 100) / 100 }
  const half = Math.max(0.03 * Math.abs(shares.centre), 0.03 * shares.size, 0.06)
  return { min: round(shares.centre - half), max: round(shares.centre + half) }
}

// HOW FAR PAST EACH OF THE THREE RULES A CANDIDATE ROW STANDS. One number per rule, positive where
// the row is outside it — a magnitude and not a verdict, which is what lets two candidates be
// compared rather than merely sorted into sendable and not.
//
// It exists for the bars. A row can be standing outside a rule before anybody has touched it —
// squeeze the revenue until the curves alone put the base under its floor and every row in the grid
// is out, whatever it looks like — and a wall that only knows "broken" cannot then tell the drag
// that eases it from the drag that makes it worse. See rowBarLimits.
//
// -Infinity for a rule that does not apply: no finite cell to measure, no attribution sum to read,
// no marketing or no floor to hold a base against. That is not a violation of size zero.
function seasonalityStrain (row, context) {
  let month = -Infinity
  for (let index = 2; index < row.length; index++) {
    const share = Number(row[index])
    if (!isFinite(share)) continue
    month = Math.max(month, Math.abs(share) - 100)
    const other = Number(context.other[index])
    if (isFinite(other)) month = Math.max(month, Math.abs(share + other) - 100)
  }

  const shares = seasonalityShares(row, context)
  const mine = seasonalityRowBand(shares, context)
  const attributions = context.attributions
  const head = Number(attributions[0][1]) + Number(attributions[1][1]) + Number(attributions[2][1])
  const otherMin = Number(attributions[context.otherAttribution][1])
  const sumMin = head + mine.min + (isFinite(otherMin) ? otherMin : 0)
  // The other end of that rule — a periodic maximum far enough below zero to leave Base with a
  // minimum above its own maximum — is not a wall any more: closeBase orders the pair, so it is a
  // row this wizard can no longer emit rather than one the user has to be kept away from.

  // BOTH ROWS COME OUT OF THE BASE, so both are subtracted here and not only the one being moved.
  // Without the neighbour's term the wall this returns and decompositionBaseWarning would be the
  // same rule giving two answers the moment the second row carried anything — the slider stopping
  // where the warning is silent, or the warning speaking over ground the slider left open.
  const base = context.marketing !== null && context.floor !== null
    ? context.floor - (context.total - context.marketing - shares.net - context.otherNet)
    : -Infinity

  return { month: month, attribution: isFinite(sumMin) ? sumMin - 100 : -Infinity, base: base }
}

// THE BAND THIS ROW WILL ACTUALLY BE WRITTEN AT, which is not the same arithmetic for the two of
// them — and the attribution rule above has to ask about the row it is moving, not about a band
// nobody will write.
//
// The periodic row is overwritten by writeSeasonalityBand after the derived rows have run, with the
// amplitude-measured band documented there. The spot row is not: it keeps what
// derivedAttributionRows gave it, the KPI-weighted share give or take the row's own variation range,
// rounded to whole points. So this reproduces each one where it lands rather than putting one band
// on both and being wrong about whichever row it was not written for.
function seasonalityRowBand (shares, context) {
  if (context.row !== 1) return seasonalityBand(shares)
  const range = Number(context.range)
  const width = isFinite(range) ? range : 0
  return {
    min: Math.round(shares.centre - width),
    max: Math.round(shares.centre + width)
  }
}

// A candidate row from a year, laid out the way the form's own row is.
function rowFromYear (year, context, months) {
  return [context.label, context.range].concat(spreadYear(year, months))
}

// ---------------------------------------------------------------------------
// THE COMPOSER'S OWN UNIT FOR THE TWO DRAWN YEARS, AND IT IS NOT THE PAYLOAD'S.
//
// Everything above this line states a seasonality as a percentage of the month's own REVENUE, which
// is what the grid carries and what the backend reads. That unit has one property the composer
// could not live with: the rule that actually binds — the base surviving what the curves are
// credited with — is ANNUAL, so twelve months of two rows are one pot the user has to ration. A
// cell typed in March is refused by what August already claims, and the wizard has to draw the pot,
// name it, warn about it and re-seat both years whenever the revenue is reshaped underneath them.
//
// Stated as a percentage of the month's own BASE instead, the same rule is LOCAL. Writing
// c = (season + spot)/100 and B for the base a month is left with once the curves have taken their
// return, the year comes to Σ B·(1 − c), so a cap of k on every month gives
//
//     base ≥ (1 − k)·(revenue − marketing)
//
// and the annual rule holds for every year that can be drawn, whatever is drawn, as long as
//
//     revenue ≥ marketing + floor/(1 − k)
//
// which is a wall on the revenue box and nothing else. That is the whole trade: one static figure
// on a box the user answers early, in exchange for a pot that disappears, a re-seating that
// disappears, and twelve months that can be typed in any order.
//
// The cap costs attribution. A model whose media are credited with more than 1/(1 + 1/(19(1−k))) of
// the revenue can no longer be composed — at 80 that is about 79%, against the 95 of
// MAX_ATTRIBUTION_SHARE — and that is a deliberate price, not an oversight: the composer is
// assembling a business out of curves, and one whose media explain four fifths of everything sold
// is not a business this wizard has anything left to say about.
// ---------------------------------------------------------------------------

// The most of a month's base either drawn year may claim, and the most the TWO of them may claim
// between them: they share the month, so it is one cap read against the sum and not two.
export const SHAPE_MONTH_CAP = 80

// The revenue floor that cap implies, as a multiple of what the curves are credited with. It is
// revenueBaseFloor said as a ratio — marketing + floor/(1 − k) over marketing, with floor being
// cloneBaseFloor's own (100 − MAX_ATTRIBUTION_SHARE)/MAX_ATTRIBUTION_SHARE — and it is stated here,
// beside the cap, because the two are one decision: raise the cap and this rises with it.
export const SHAPE_REVENUE_FACTOR =
  1 + (100 - MAX_ATTRIBUTION_SHARE) / MAX_ATTRIBUTION_SHARE / (1 - SHAPE_MONTH_CAP / 100)

// WHAT EACH MONTH IS LEFT WITH once the curves have been credited with what they return in it: the
// base the two drawn years take their share of, and the figure every percentage in the composer's
// seasonality section is a percentage OF.
//
// Floored at zero rather than allowed negative. A month whose curves return more than the whole of
// it has no base to take a share of, and a negative one would turn a positive percentage into a
// claim that GAVE revenue back — a spot event drawn as a rise appearing in the picture as a fall.
// The bar limits below read that zero as a month that cannot be typed into at all, and what is
// actually wrong there is a spend and a revenue, which is what decompositionBaseWarning says.
//
// Null, never a row of zeros, wherever cloneMarketingMonthly is — the silence a missing curve keeps.
export function cloneBaseMonthly (form) {
  if (!form || !form.kpi) return null
  const kpi = ((form.kpi.data || [])[0] || []).slice(2).map(Number)
  if (kpi.length === 0) return null
  // A form with NO CHANNELS AT ALL is a base of the whole revenue rather than no reading: the
  // composer draws a year before it can read a curve, and a section that went silent for want of
  // media would be the editor claiming the media decide whether a business has a season. A form
  // whose channels are there but whose curve cannot be read is the other answer — the silence
  // cloneMarketing keeps — because that reading is missing and not zero.
  const media = (form.reference && form.reference.media) || []
  const marketing = media.length === 0 ? null : cloneMarketingMonthly(form, 1)
  if (media.length > 0 && marketing === null) return null
  return kpi.map(function (total, month) {
    const revenue = isFinite(total) ? total : 0
    const spend = marketing === null ? 0 : Number(marketing[month])
    const base = revenue - (isFinite(spend) ? spend : 0)
    return base > 0 ? base : 0
  })
}

// A YEAR DRAWN IN THE COMPOSER'S UNIT, WRITTEN IN THE PAYLOAD'S. Month by month, because the two
// units differ by that month's own ratio of base to revenue and by nothing else:
//
//     share_of_revenue = share_of_base · B/R
//
// Rounded into the two decimals a cell carries here rather than by the caller, so what is tested
// against the walls and what lands in the grid are the same twelve numbers.
//
// It cannot overflow the payload's own month rule. B < R wherever the curves return anything at
// all, so a year capped at SHAPE_MONTH_CAP of the base lands strictly inside that fraction of the
// revenue, and the pair validateEkrmForm adds up per month is inside a hundred by construction.
//
// Null wherever cloneBaseMonthly is, which is the caller's cue to write nothing.
export function cloneShapeCells (form, year) {
  const drawn = seasonalityYear(year)
  const base = cloneBaseMonthly(form)
  if (!drawn || !base) return null
  const kpi = ((form.kpi.data || [])[0] || []).slice(2).map(Number)
  return base.map(function (money, month) {
    const revenue = Number(kpi[month])
    if (!isFinite(revenue) || !(revenue > 0)) return 0
    const share = Number(drawn[month % 12])
    if (!isFinite(share)) return 0
    return Math.round(100 * share * money / revenue) / 100
  })
}

// THE INTERVAL ONE MONTH OF A DRAWN YEAR MAY BE MOVED THROUGH, in the composer's unit and with the
// other eleven — and the other row — standing still. The counterpart of cloneSeasonalityBarLimits,
// which is still what the clone wizard asks: that one holds a bar inside three rules of which the
// binding one is annual, and this one inside two, of which the binding one is the month itself.
//
// `shapes` is BOTH drawn years, in the order they are drawn — the periodic first, the spot second —
// because the cap is read against their sum in this month and neither can be recovered from the
// grid: the grid carries them converted, and dividing a converted cell back by a ratio that may be
// zero is an arithmetic nobody should have to trust.
//
// The two rules, in the order a reason is taken from them:
//
//   month         |v| ≤ cap, and |v + other| ≤ cap. Analytic, and it is the wall almost every
//                 gesture meets. A month with no base left is pinned at zero by the same rule.
//   attribution   the minima of the payload's five attribution rows still closing under a hundred.
//                 Annual, unavoidably — a band is a reading of the whole year — and bisected
//                 against the row AS IT WILL BE WRITTEN, so the wall stands where the payload does.
//
// The base rule is gone, and that is the point of the unit: SHAPE_REVENUE_FACTOR holds the revenue
// box above the figure that makes it true for every year inside the cap, so no cell can break it.
export function cloneShapeBarLimits (form, shapes, index, rowIndex) {
  const which = rowIndex === 1 ? 1 : 0
  const mine = seasonalityYear((shapes || [])[which])
  const other = seasonalityYear((shapes || [])[1 - which])
  if (!mine || !(index >= 0) || index > 11) return null
  const now = Number(mine[index]) || 0
  const neighbour = other ? Number(other[index]) || 0 : 0

  // The cap, and the month with nothing left to take a share of. Both ends always contain the value
  // the bar is standing on, which pins a bar the other row has already crowded rather than letting
  // this one be walked further out.
  const base = cloneBaseMonthly(form)
  const empty = base !== null && index < base.length && !(base[index] > 0)
  const capLow = empty ? 0 : Math.max(-SHAPE_MONTH_CAP, -SHAPE_MONTH_CAP - neighbour)
  const capHigh = empty ? 0 : Math.min(SHAPE_MONTH_CAP, SHAPE_MONTH_CAP - neighbour)

  const context = seasonalityContext(form, which)
  if (!context) {
    return {
      min: { value: Math.min(now, capLow), reason: null },
      max: { value: Math.max(now, capHigh), reason: null }
    }
  }

  // The attribution strain of this row with one month moved, read off the row the payload would
  // carry: the candidate year converted through cloneShapeCells and laid out the way the grid lays
  // a row out. Only the attribution term is asked of it — the month rule is the cap above, in the
  // other unit, and the base rule is not a rule here at all.
  const months = form.months || null
  const strainAt = function (value) {
    const candidate = mine.slice()
    candidate[index] = value
    const cells = cloneShapeCells(form, candidate)
    if (!cells) return -Infinity
    return seasonalityStrain(rowFromYear(cells, context, months), context).attribution
  }

  // NO WORSE RATHER THAN NOT BROKEN, for the reason rowBarLimits gives at length: a row can be
  // standing outside this rule before anybody has touched it — drive the spend until the marketing
  // band alone crowds the five — and a wall that only knows "broken" freezes the move that would
  // ease it along with the move that would make it worse.
  const standing = strainAt(now)
  const broken = standing >= 0
  const holds = broken
    ? function (value) { const found = strainAt(value); return found < 0 || found <= standing + 1e-9 }
    : function (value) { return strainAt(value) < 0 }

  const endOf = function (up) {
    const cap = up ? capHigh : capLow
    if (holds(cap)) return { value: up ? Math.max(now, cap) : Math.min(now, cap), reason: null }
    let inside = now
    let outside = cap
    for (let pass = 0; pass < SOLVE_ITERATIONS; pass++) {
      const middle = (inside + outside) / 2
      if (holds(middle)) inside = middle
      else outside = middle
    }
    const trimmed = up ? Math.floor(inside * 100) / 100 : Math.ceil(inside * 100) / 100
    return {
      value: up ? Math.max(now, trimmed) : Math.min(now, trimmed),
      reason: LIMIT_ATTRIBUTION
    }
  }
  return { min: endOf(false), max: endOf(true) }
}

// ---------------------------------------------------------------------------
// The curves, moved
// ---------------------------------------------------------------------------

// How finely a lever's range is searched for the point at which it stops being a payload the wizard
// will send. Sampled first and bisected after — where the seasonality bars go straight to a
// bisection — because marketing is NOT monotone in the exponent: a Hill curve with an n above one is
// convex below its knee, so raising n lowers the response at thin spend and raises it near the knee,
// and a bisection walking outward from the current value would step over a wall and report the far
// end as clear. The same reason SOLVE_SAMPLES exists.
const CURVE_SAMPLES = 41
const CURVE_ITERATIONS = 30

// The three sliders' ranges, which are hillCurve's own — the expert wizard's four minus the
// saturation point, which this wizard does not offer: with the curves travelling, the point only
// places the ten grid columns and the backend does not read them.
//
// `imported` anchors the range and `current` is only there to widen it, so a channel dragged to the
// end of its ceiling slider does not find the range moving out from under it on the next rebuild.
export function cloneCurveBounds (imported, current, maxSpend) {
  const reference = isHillParams(imported) ? imported : current
  const bounds = sliderBounds({
    reference: reference,
    current: current,
    point: current && isFinite(current.point) ? current.point : 0,
    maxSpend: maxSpend
  })
  return { vmax: bounds.vmax, k: bounds.k, n: bounds.n }
}

// Everything that does NOT move while one channel's curve is tried, gathered once so a search does
// not re-read eleven other channels' curves four hundred times.
//
// `centre` is the marketing attribution the form carries before the row rounded it, and it is asked
// for rather than read: at the bottom of the range the two decimals a cell holds are most of the
// number, and a limit read off the row lands several percent inside itself.
function curveContext (form, index, options) {
  const media = (form && form.reference && form.reference.media) || []
  const channels = form ? roasChannels(form) : null
  if (!channels || !channels[index] || !form.kpi || !form.attributions) return null
  const attributions = form.attributions.data || []
  if (attributions.length < 5) return null

  const kpi = (form.kpi.data[0] || []).slice(2).map(Number)
  const total = sum(kpi)
  const row = attributions[0] || []
  const exact = Number((options || {}).centre)
  const centre = isFinite(exact) && exact > 0 ? exact : (Number(row[1]) + Number(row[2])) / 2
  if (!(total > 0) || !(centre > 0)) return null

  let others = 0
  let mine = 0
  for (let i = 0; i < channels.length; i++) {
    const channel = channels[i]
    let response = 0
    for (let month = 0; month < channel.cells.length; month++) {
      response += responseAt(channel.curve, channel.cells[month])
    }
    if (i === index) mine = response
    else others += response
  }
  if (!(others + mine > 0)) return null

  // BOTH signed rows, because both come out of the base this corridor is holding above its floor.
  // The spot row is blank in the clone wizard and reads null there, which takes nothing.
  const seasonality = cloneSeasonality(form, kpi, 0)
  const spot = cloneSeasonality(form, kpi, 1)
  const curves = (form.curves || {})[media[index]] || {}
  const stated = Number((options || {}).floor)
  return {
    spend: channels[index].cells,
    source: (curves.source || {}).saturation || null,
    others: others,
    marketingNow: others + mine,
    total: total,
    centre: centre,
    attributions: attributions,
    // The least the curve's own two figures may come to, in the payload's money a month, or zero
    // for a caller that states no floor. See curveBreaks. It is the composer's constant and it
    // arrives as an option rather than as an import: the clone places its curves off a model that
    // was measured, where this wall is nowhere near anything, and a wizard's own floor is not a
    // fact about every form this file reads.
    floor: isFinite(stated) && stated > 0 ? stated : 0,
    // Signed, and the same reading the waterfall draws — the two rows summed, which is what the
    // waterfall subtracts from its base and what decompositionBaseWarning subtracts from its own.
    // Null on either means a row saying nothing, which takes nothing from the base.
    seasonality: (seasonality === null ? 0 : seasonality) + (spot === null ? 0 : spot)
  }
}

// What the whole plan's marketing comes to with this one channel read at these parameters — off the
// curve the payload WOULD carry, read the way the payload's own curve is read, so a limit lands
// where the verdict actually changes rather than near it.
function curveMarketing (params, context) {
  const curve = hillCurvePoints(params, context.source)
  if (!curve) return null
  let response = 0
  for (let month = 0; month < context.spend.length; month++) {
    response += responseAt(curve, context.spend[month])
  }
  return context.others + response
}

// Which rule a candidate curve breaks, or null for one the wizard will send. The three that a curve
// can reach:
//
//   attribution  the marketing band this payload would carry, which is proportional to what the
//                curves return — so it is read off the band the form already carries, the source's
//                own attribution and the revenue factor behind it cancelling in the ratio. Upward it
//                stops at MAX_ATTRIBUTION_SHARE and at the attribution rows no longer closing on a
//                hundred; downward at a band whose minimum is no longer positive, which the
//                validator refuses.
//   base         the curves credited with more revenue than there is, once the seasonality and the
//                spot events have taken their share: cloneBaseFloor, the floor the media spend is
//                already held above, asked of a curve instead of a level.
//   floor        the curve's OWN two figures — where it saturates and what it generates there —
//                fallen under the least the caller states a figure at, or the whole plan's media
//                returning less than that floor a month between them. Only a caller that states a
//                floor can reach either.
//
// THE FLOOR IS THE ONE RULE THAT IS ABOUT THIS CURVE ALONE, and it is here rather than beside the
// lever ranges because a lever's range is a statement about one number while this is a statement
// about the curve the three of them come to. It has to be a wall and not a rounding guard: the
// ranges are read against the fit the channel currently carries, so every commit re-anchors them —
// the ceiling lever reaching a quarter of where it stands, the knee lever a tenth — and a rule that
// only refused the arithmetic's own zero would be walked up to, one commit at a time, until the
// payload carried a saturation point that rounds away. Which the validator refuses outright, and
// which no reader typed.
//
// LIMIT_REVENUE is not among them. Spending past the revenue is a rule about the spend, and a curve
// edit moves no spend cell.
function curveBreaks (params, context) {
  if (context.floor > 0) {
    // A hair under the floor, for the reason every wall in this file stands a hair inside itself: a
    // channel stated at the floor exactly carries a curve whose own two figures are that floor to
    // floating point, and a rule that refused it would pin all three of its levers where they stand.
    const wall = LIMIT_MARGIN * context.floor
    const at = hillDiminishingReturns(params)
    if (!(at >= wall) || !(hillAt(params, at) >= wall)) return LIMIT_FLOOR
  }

  const marketing = curveMarketing(params, context)
  if (marketing === null || !(marketing > 0)) return LIMIT_ATTRIBUTION

  // What the media return between them, against the same floor — and it is a separate rule from the
  // attribution below it, for the reason the spend corridor's own money floor is separate. In the
  // wizard that states a floor the revenue TRACKS the media until it is typed over, so the
  // attribution is pinned
  // at a tenth however far a lever takes the response, the wall is the same distance away after
  // every commit, and the levers walk a channel down through it a factor at a time. This one does
  // not move: it is the money itself.
  if (context.floor > 0 && !(marketing >= LIMIT_MARGIN * context.floor * context.spend.length)) {
    return LIMIT_FLOOR
  }

  const centre = context.centre * marketing / context.marketingNow
  if (!(centre > 0) || centre >= MAX_ATTRIBUTION_SHARE) return LIMIT_ATTRIBUTION
  const marketingBand = band(centre)
  if (!(marketingBand.min > 0)) return LIMIT_ATTRIBUTION

  let sumMin = marketingBand.min
  for (let row = 1; row < 5; row++) {
    const value = Number(context.attributions[row][1])
    if (isFinite(value)) sumMin += value
  }
  if (sumMin >= 100) return LIMIT_ATTRIBUTION

  const floor = cloneBaseFloor(marketing)
  if (floor !== null && context.total - marketing - context.seasonality < floor) return LIMIT_BASE
  return null
}

// The interval one lever may be moved through with the other two standing still, in the units the
// lever is drawn in. Both ends always contain the value the lever stands on, so a form already past
// a rule pins its levers rather than letting a drag walk them further out.
function leverLimits (key, params, range, context) {
  const now = Number(params[key])
  const breaksAt = function (value) {
    const candidate = { vmax: params.vmax, k: params.k, n: params.n }
    candidate[key] = value
    return isHillParams(candidate) ? curveBreaks(candidate, context) : LIMIT_ATTRIBUTION
  }
  const pinned = { value: now, reason: null }
  if (breaksAt(now) !== null) return { min: pinned, max: pinned }

  const endOf = function (up) {
    const far = range ? Number(up ? range.max : range.min) : NaN
    if (!isFinite(far) || (up ? far <= now : far >= now)) return { value: now, reason: null }

    let inside = now
    let outside = null
    let reason = null
    for (let step = 1; step <= CURVE_SAMPLES; step++) {
      const value = now + (far - now) * step / CURVE_SAMPLES
      const found = breaksAt(value)
      if (found !== null) {
        outside = value
        reason = found
        break
      }
      inside = value
    }
    // Nothing binds inside the slider's own range, which is the usual answer for the knee and the
    // exponent: the lever simply runs to its end.
    if (outside === null) return { value: far, reason: null }

    for (let pass = 0; pass < CURVE_ITERATIONS; pass++) {
      const middle = (inside + outside) / 2
      const found = breaksAt(middle)
      if (found !== null) {
        outside = middle
        reason = found
      } else {
        inside = middle
      }
    }
    return { value: inside, reason: reason }
  }
  return { min: endOf(false), max: endOf(true) }
}

// How far each of the three levers may be moved, with the other two standing still.
//
// Bounded rather than warned about, which is the convention every rail in this wizard follows: what
// is held inside the corridor is the VALUE, not the slider's own range, so the thumb goes on
// tracking the number under it and the ground it may not enter is greyed on the track.
//
// `params` is where the levers stand now — the built form's own, so the limits are a statement about
// the payload as it is rather than about the one the import made.
//
// `options.centre` is the band's centre before the row rounded it, as everywhere else here.
// `options.floor` is the least a figure may be stated at in the wizard asking — the composer states
// one, the clone does not — and it is the wall LIMIT_FLOOR marks.
export function cloneCurveLimits (form, index, params, bounds, options) {
  if (!isHillParams(params) || !bounds) return null
  const context = curveContext(form, index, options)
  if (!context) return null
  const limits = {}
  for (let i = 0; i < CURVE_KEYS.length; i++) {
    const key = CURVE_KEYS[i]
    limits[key] = leverLimits(key, params, bounds[key], context)
  }
  return limits
}

// How far either side of the source model's own media spend the summary's trade-off chart reaches,
// and how far past that it will stretch to bring an end of the allowed range into view. Two decades
// is wide enough that the peak of the return is almost always on screen — which is the thing worth
// seeing — and narrow enough to drag in precisely.
const WINDOW_LOW = 0.1
const WINDOW_HIGH = 10
const WINDOW_REACH = 3
// A plan within this much of the window's own edge counts as standing ON it rather than outside it.
const WINDOW_EDGE = 1e-6

// The stretch of one of the summary's two axes that its chart is drawn across and its slider runs
// along, as multipliers on the SOURCE model's own figure. `level` is where the plan stands in those
// same terms and `limits` is what the corridor readers said about the form standing there. Nothing here knows which of the two it is being asked about: both are a level
// against the source's own, both are bounded by what the wizard will accept, and both have to hold
// still under a slider for the same reason.
//
// Everything about it is a property of the source model and of what the wizard will accept — never
// of where the plan currently is. That is the whole point: the axis has to hold still while the
// slider moves along it, or every mark on the chart creeps a little each time the slider is let go,
// and the greyed ground grows with them.
//
// The one exception is a plan that has been typed PAST all of it, which the window has to open up to
// hold — the boxes are not bounded by the corridor, only the slider is. Standing exactly on an edge
// is not that: sliding hard against either end lands there, and making room for a plan already in
// the frame is what makes the axis crawl.
export function cloneTradeWindow (level, limits) {
  const plan = Number(level) > 0 ? Number(level) : 1
  let from = WINDOW_LOW
  let to = WINDOW_HIGH

  // A little past a limit rather than exactly on it, so its rule stands inside the chart and not on
  // the frame — and only when it is close enough to be worth reaching for. A ceiling a hundred times
  // past anything anyone would spend has to stay off the chart, or the range worth dragging in is
  // squeezed into a sliver.
  const low = limits && limits.min ? limits.min.factor * plan * 0.85 : null
  const high = limits && limits.max ? limits.max.factor * plan * 1.15 : null
  if (low !== null && low < from && low >= from / WINDOW_REACH) from = low
  if (high !== null && high > to && high <= to * WINDOW_REACH) to = high

  if (plan < from * (1 - WINDOW_EDGE)) from = plan * 0.8
  if (plan > to * (1 + WINDOW_EDGE)) to = plan * 1.25
  return { from: from, to: to }
}

// ---------------------------------------------------------------------------
// The mix
// ---------------------------------------------------------------------------

// The multipliers one channel may be given, and why the two ends are where they are. Both come off
// the channel's own curve, because the curve is the only thing that knows what its spend is worth,
// and both ends of it are flat — which is safe at neither end and dangerous at one.
//
// Upper: the largest month has to stay inside the span the curve describes. Past its last point
// responseAt is flat, so money spent there is priced by a curve that never saw it: the EKRM would be
// told the channel returns nothing at all for the top of its budget, which is not a measurement but
// the end of one. Never below 1 — a channel the source already reads past its own curve is not made
// to shrink for it.
//
// Lower: the smallest spend the curve describes, against the channel's average live month. Below its
// first point responseAt is flat too, and flat at the BOTTOM is the dangerous end: a channel taken to
// nothing would keep the whole of its first point's response and report an unbounded return on a
// spend of almost zero — which is exactly the answer a solver hunting for a high ROAS would find and
// hand back. Never above 1, so a channel whose curve starts above its own average month is left
// where it is rather than forced up.
//
// MIN_MIX_FACTOR floors both. A curve that starts at the origin — most do — has nothing to say about
// the bottom, and the floor is then the only thing standing between the solver and a medium emptied
// out of the EKRM entirely.
function mixBounds (channel) {
  const live = channel.cells.filter(function (cell) { return cell > 0 })
  const meanLive = live.length > 0 ? sum(live) / live.length : 0
  const maxLive = live.length > 0 ? Math.max.apply(null, live) : 0
  const x = channel.curve.x
  const firstX = Number(x[0])
  const lastX = Number(x[x.length - 1])
  const low = Math.max(
    MIN_MIX_FACTOR,
    firstX > 0 && meanLive > 0 ? Math.min(1, firstX / meanLive) : MIN_MIX_FACTOR
  )
  const high = lastX > 0 && maxLive > 0 ? Math.max(1, lastX / maxLive) : 1
  return { low: low, high: Math.max(high, low) }
}

// The rungs one channel may be moved between, with its spend and its response at each already worked
// out: the frontier below is walked a few dozen times over the same ladders and nothing in them
// moves.
function mixLadder (channel) {
  const bounds = mixBounds(channel)
  const logLow = Math.log(bounds.low)
  const step = (Math.log(bounds.high) - logLow) / (MIX_CANDIDATES - 1)
  const factors = []
  for (let index = 0; index < MIX_CANDIDATES; index++) factors.push(Math.exp(logLow + index * step))
  // The source's own level, exactly. A channel the solver has no reason to move has to come back at
  // one rather than at the nearest rung to it.
  if (bounds.low <= 1 && bounds.high >= 1) factors.push(1)
  return factors.map(function (factor) {
    let response = 0
    for (let month = 0; month < channel.cells.length; month++) {
      response += responseAt(channel.curve, channel.cells[month] * factor)
    }
    return { factor: factor, spend: factor * channel.total, response: response }
  })
}

// The mix one shadow price picks: every channel taking money up to the point where its next unit
// returns less than the price of it, which is the rule the plan optimizer follows and the definition
// of an efficient mix — at the answer, the last unit spent on each channel is worth the same as the
// last unit spent on every other. Ties go to the rung nearest the source's own level.
function mixAtPrice (ladders, price) {
  return ladders.map(function (ladder) {
    let best = ladder[0]
    let bestValue = null
    for (let index = 0; index < ladder.length; index++) {
      const rung = ladder[index]
      const value = rung.response - price * rung.spend
      const nearer = value === bestValue &&
        Math.abs(Math.log(rung.factor)) < Math.abs(Math.log(best.factor))
      if (bestValue === null || value > bestValue || nearer) {
        best = rung
        bestValue = value
      }
    }
    return best.factor
  })
}

// A mix rescaled to leave the total spend where it was, so that what tells two mixes apart is the
// mix alone and the level is left to solveScale. Weights, not factors, after this.
function normaliseMix (weights, channels) {
  let moved = 0
  let base = 0
  for (let index = 0; index < channels.length; index++) {
    moved += weights[index] * channels[index].total
    base += channels[index].total
  }
  if (!(moved > 0) || !(base > 0)) return weights
  const level = moved / base
  return weights.map(function (weight) { return weight / level })
}

// How far a mix has moved off the one the form carries: the spend-weighted mean of |log factor|, so
// a channel carrying a tenth of the budget counts for a tenth of the answer and a doubling counts
// the same as a halving. Zero for a mix that has not moved at all.
function mixDistance (weights, channels) {
  let weighted = 0
  let base = 0
  for (let index = 0; index < channels.length; index++) {
    weighted += channels[index].total * Math.abs(Math.log(weights[index]))
    base += channels[index].total
  }
  return base > 0 ? weighted / base : 0
}

// The prices at which one channel changes its mind, and there are no others.
//
// At any price a channel takes the rung that maximises R − λS, which is a vertex of the upper convex
// hull of its own (spend, response) points; the price at which it steps from one vertex to the next
// is the slope between them. So the hull's slopes, unioned over the channels, are every price the
// frontier has a different answer at, and everything between two of them gives the same mix twice.
//
// Sampling a ladder of prices instead misses the mixes that live in a narrow window — and the narrow
// windows are exactly the interesting ones, being where one channel has given up and another has
// not. Two channels whose best average returns are 16.5 and 21.2 differ by a factor of 1.3, which a
// logarithmic ladder wide enough to cover free money jumps straight over.
function hullSlopes (ladder) {
  const points = ladder.slice().sort(function (a, b) { return a.spend - b.spend })
  const hull = []
  points.forEach(function (point) {
    while (hull.length >= 2) {
      const last = hull[hull.length - 1]
      const before = hull[hull.length - 2]
      const cross = (last.spend - before.spend) * (point.response - before.response) -
        (last.response - before.response) * (point.spend - before.spend)
      if (cross < 0) break
      hull.pop()
    }
    hull.push(point)
  })
  const slopes = []
  for (let index = 1; index < hull.length; index++) {
    const run = hull[index].spend - hull[index - 1].spend
    if (run > 0) slopes.push((hull[index].response - hull[index - 1].response) / run)
  }
  return slopes
}

// Every mix the frontier has, and each of them once: the price zero, where every channel takes all
// its curve can price; one price between each pair of consecutive breakpoints, geometrically so that
// two prices an order of magnitude apart are not read as adjacent; and one past the last, where
// every channel has given up. Mixes that come out identical are dropped — a price no channel changes
// its mind at is a mix already in the list, and each one costs a full solve below.
function mixCandidates (channels) {
  const ladders = channels.map(mixLadder)
  const breaks = []
  ladders.forEach(function (ladder) {
    hullSlopes(ladder).forEach(function (slope) { if (slope > 0) breaks.push(slope) })
  })
  breaks.sort(function (a, b) { return a - b })

  const prices = [0]
  for (let index = 0; index < breaks.length; index++) {
    const next = index + 1 < breaks.length ? breaks[index + 1] : breaks[index] * 2
    if (next > breaks[index]) prices.push(Math.sqrt(breaks[index] * next))
  }

  const seen = {}
  const candidates = []
  prices.forEach(function (price) {
    const weights = normaliseMix(mixAtPrice(ladders, price), channels)
    const key = weights.map(function (weight) { return weight.toPrecision(6) }).join('|')
    if (seen[key]) return
    seen[key] = true
    candidates.push(weights)
  })
  if (candidates.length <= MIX_MIXES) return candidates

  // Both ends kept, because they are the two the frontier is bounded by: everything the curves can
  // price, and nothing but the floor.
  const thinned = []
  const stride = (candidates.length - 1) / (MIX_MIXES - 1)
  for (let index = 0; index < MIX_MIXES; index++) {
    thinned.push(candidates[Math.round(index * stride)])
  }
  return thinned
}

// The levels a mix may be read at with every channel still inside the range its own curve prices.
//
// The bounds are per channel and the level is common, so the level is what is left of them once the
// mix has taken its share: a channel already sitting at the top of its range holds the whole plan
// back from growing. Null when the mix leaves no room at all, which is a mix to drop rather than a
// mix to level.
//
// This is the difference between the two ways spend moves in this wizard. The Media Spend box is a
// user saying how much they intend to spend, and it is bounded by nothing but the bracket it has
// always had; the mix is a solver hunting for a return, and a solver let loose past the ends of a
// curve finds its answer where the curve stopped being a measurement.
function mixBracket (weights, bounds) {
  let low = SOLVE_MIN_FACTOR
  let high = SOLVE_MAX_FACTOR
  for (let index = 0; index < weights.length; index++) {
    low = Math.max(low, bounds[index].low / weights[index])
    high = Math.min(high, bounds[index].high / weights[index])
  }
  return high > low ? { min: low, max: high } : null
}

// The per-channel multipliers that make this form report `targetRoas` when its own mix cannot.
//
// Two things at once, in this order: a MIX, taken off the efficient frontier — every channel fed up
// to the point where its next unit is worth the same as every other channel's, which is what the
// plan optimizer would do with this money — and then a LEVEL on top of it, from the same solveScale
// the ROAS box already uses. The level is what lands the target exactly; the mix is only there to
// bring it within reach.
//
// Among the frontier mixes that do bring it within reach, the one that moves the mix LEAST is
// returned. The user asked for a return, not for a reallocation: the smallest change that answers
// them is the one they meant, and the source's mix is what this wizard exists to copy.
//
// No curve moves, here or anywhere else in this file. Every channel is still read off the response
// the model measured for it — at a different point of it, one point per channel instead of one for
// all of them, and never outside the span that response actually covers (mixBounds).
//
// Returns one factor per medium, in the order form.reference.media carries them, or null when no mix
// on the frontier can reach the target either.
export function solveSpendMix (form, targetRoas) {
  const target = Number(targetRoas)
  const channels = roasChannels(form)
  if (!channels || !isFinite(target) || target <= 0) return null

  // Nearest the source's own mix first, and the first that answers wins: every mix behind it in this
  // queue moves the money further for the same return.
  const bounds = channels.map(mixBounds)
  const ranked = mixCandidates(channels).map(function (weights) {
    return { weights: weights, distance: mixDistance(weights, channels) }
  }).sort(function (a, b) { return a.distance - b.distance })

  for (let index = 0; index < ranked.length; index++) {
    const weights = ranked[index].weights
    const bracket = mixBracket(weights, bounds)
    if (!bracket) continue
    // Sifted first and solved second: a dozen channels put a few hundred mixes on this queue, and
    // asking each of them for an exact level would be most of a second of the user's time.
    const reach = reachOf(channels, weights, bracket, MIX_SAMPLES)
    if (!reach || target < reach.min || target > reach.max) continue
    const scale = solveScale(channels, weights, target, bracket)
    if (scale !== null) return weights.map(function (weight) { return weight * scale })
  }
  return null
}

// The best and the worst Total ROAS these channels can be made to report once the mix is free to
// move as well: the range cloneRoasReach gives, widened by every mix on the frontier. What the
// wizard says when not even a reallocation can reach what was asked for.
export function cloneMixReach (form) {
  const channels = roasChannels(form)
  if (!channels) return null
  let min = null
  let max = null
  const consider = function (reach) {
    if (!reach) return
    if (min === null || reach.min < min) min = reach.min
    if (max === null || reach.max > max) max = reach.max
  }
  // The mix the form already carries, over the whole bracket the Media Spend box can reach — so the
  // range this returns always contains the one cloneRoasReach gives, and a message quoting both
  // never has the wider of the two coming out narrower.
  consider(reachOf(channels, null))
  const bounds = channels.map(mixBounds)
  mixCandidates(channels).forEach(function (weights) {
    consider(reachOf(channels, weights, mixBracket(weights, bounds), MIX_SAMPLES))
  })
  return min === null ? null : { min: min, max: max }
}

// What the EKRM will fit this medium's response on: the source curve the payload carries, or — when
// curvePayload rejected it for having fewer than two points — the ten-point grid, which is what the
// backend falls back to for exactly the same medium. Null when neither is there to read.
function mediumResponseCurve (form, index, medium) {
  const curves = (form.curves || {})[medium]
  const source = curves && curves.source ? curves.source.saturation : null
  if (source && Array.isArray(source.x) && source.x.length > 1) return source

  const header = form.saturation.data[2 * index] || []
  const revenue = (form.saturation.data[2 * index + 1] || []).slice(4).map(Number)
  const point = Number(header[3])
  if (!(point > 0) || revenue.length === 0) return null
  return { x: gridSpends(point), y: revenue }
}

// A monotone curve read at one spend, linearly between its points and flat past either end.
//
// Nothing spent returns nothing, ahead of any of that. A source curve need not start at the origin —
// most start at the smallest spend the model ever saw — and reading a month of zero as the response
// at that first point would credit a channel for money it was not given. It matters twice now: a
// flighted channel has dark months of its own, and the wizard lets a channel be taken out of the
// plan entirely by setting its spend to zero.
export function responseAt (curve, spend) {
  const x = curve.x
  const y = curve.y
  const value = Number(spend)
  if (!isFinite(value) || value <= 0) return 0
  if (value <= x[0]) return Number(y[0]) || 0
  for (let i = 1; i < x.length; i++) {
    if (value <= x[i]) {
      const span = x[i] - x[i - 1]
      const at = span > 0 ? (value - x[i - 1]) / span : 0
      return Number(y[i - 1]) + at * (Number(y[i]) - Number(y[i - 1]))
    }
  }
  return Number(y[y.length - 1]) || 0
}

// The pairs of a series that can actually be plotted, as numbers.
function finitePairs (points) {
  return (points || []).filter(function (pair) {
    return pair && isFinite(Number(pair[0])) && isFinite(Number(pair[1]))
  }).map(function (pair) { return [Number(pair[0]), Number(pair[1])] })
}

// The decay kernel as bars, normalised against its own peak so a kernel that carries a fifth of the
// response and one that carries three fifths are read as the shapes they are.
//
// `slots` is how many lag columns the box is divided into, and both wizards pass the LONGEST kernel
// among the channels they are drawing rather than each channel's own length. The bars are then the
// same width on every card and month three stands at the same place on all of them, which is what
// makes a row of cards comparable; sized to each card's own kernel, a three-month channel drew
// three fat bars beside a twelve-month channel's twelve thin ones and the difference in width read
// as a difference in the response.
//
// Nothing is drawn for a channel with no kernel or a kernel that is flat at zero: the card says "no
// curve" there, which is a different statement from a floor of empty bars.
export function decayBars (curve, width, height, slots) {
  const values = curve && Array.isArray(curve.y) ? curve.y.map(Number) : []
  if (values.length === 0) return []
  const peak = Math.max.apply(null, values)
  if (!(peak > 0)) return []
  const columns = Math.max(Number(slots) > 0 ? Number(slots) : 0, values.length)
  const slot = width / columns
  return values.map(function (value, index) {
    const bar = Math.max(0, (value / peak) * height)
    return {
      x: index * slot + slot * 0.15,
      y: height - bar,
      width: slot * 0.7,
      height: bar
    }
  })
}

// An SVG path through a series, normalised into a box. Returned as two strings: the line itself and
// the same line closed along the bottom edge, which is what fills under it.
//
// Both axes are normalised against the series' own range rather than a shared one, because these are
// thumbnails of a shape — a channel spending a thousandth of another's would otherwise be a flat
// line against the axis. Fewer than two points has no shape to draw and comes back null.
//
// `options.commonScale` is the exception, and it is about the TWO AXES of one plot rather than about
// two plots: a saturation curve is dollars against dollars, so scaling its x and its y separately
// lays two rulers of different lengths on one quantity and a channel returning half what it costs
// draws the same picture as one returning three times it. Given it, both axes run from zero to the
// larger of the two maxima and the shape is read against the box — which is only honest if the
// caller has also drawn the curve out that far; see channelPlotSeries' `level`.
//
// `extra` is a second series that sets the SCALE without being drawn, and it exists for the one case
// where two lines share a box: a channel's edited curve and the curve its model measured. Normalised
// separately the two would each fill the box and an edit would be invisible; normalised together
// they are read against each other, and neither can leave the viewport — a lowered ceiling drawn on
// its own scale would put the source line above the top edge, where the svg simply clips it away.
// Called twice with the two series swapped it gives the two paths on one scale, the union of the two
// ranges being the same set either way.
export function sparklinePath (points, width, height, extra, options) {
  const pairs = finitePairs(points)
  if (pairs.length < 2) return null

  const common = !!(options && options.commonScale)
  const scale = extra ? pairs.concat(finitePairs(extra)) : pairs
  const xs = scale.map(function (pair) { return pair[0] })
  const ys = scale.map(function (pair) { return pair[1] })
  // The response axis is anchored at zero rather than at the smallest value: a saturation curve read
  // between its own first and last point would look like a straight climb whatever its shape.
  const floor = Math.min(0, Math.min.apply(null, ys))
  // ONE range on a shared scale, at both ends. A ruler that began at the smallest spend drawn and
  // ended at the largest response is the same ruler shifted, which is not the same ruler — so the
  // two axes take the lower of the two origins and the higher of the two maxima, and a dollar spans
  // the same fraction of the box either way it is measured.
  const base = common ? Math.min(floor, Math.min.apply(null, xs)) : 0
  const reach = Math.max(Math.max.apply(null, xs), Math.max.apply(null, ys))
  const minX = common ? base : Math.min.apply(null, xs)
  const minY = common ? base : floor
  const maxX = common ? reach : Math.max.apply(null, xs)
  const maxY = common ? reach : Math.max.apply(null, ys)
  const spanX = maxX - minX
  const spanY = maxY - minY

  const project = function (pair) {
    const x = spanX > 0 ? (pair[0] - minX) / spanX * width : 0
    const y = spanY > 0 ? height - (pair[1] - minY) / spanY * height : height
    return [Math.round(x * 100) / 100, Math.round(y * 100) / 100]
  }
  const projected = pairs.map(project)
  const line = projected.map(function (point, index) {
    return (index === 0 ? 'M' : 'L') + point[0] + ' ' + point[1]
  }).join(' ')
  const first = projected[0]
  const last = projected[projected.length - 1]
  return {
    line: line,
    area: line + ' L' + last[0] + ' ' + height + ' L' + first[0] + ' ' + height + ' Z',
    project: project
  }
}
